import { DatePicker } from '@3plearning/react-components';
import {
    ALL_STUDENTS,
    ALL_GROUPS,
    ALL_CLASSES,
    SELECTED_CLASS,
    SELECTED_GROUP,
    SELECTED_STUDENT,
    SELECTED_REPORT,
    SELECTED_DATE,
    SELECTED_YEAR_GROUP,
    SELECTED_TEACHER,
    ALL_TEACHERS,
    ALL_YEARS
} from '../components/report-filters/filter-constants';
import UrlHelper from './url-helper';
import {
    CLASS_PATH,
    GROUP_PATH,
    STUDENT_PATH,
    DATE_TYPE_MAP,
    DATE_PARAM_MAP,
    REPORT_TYPE_TO_PATH_MAP,
    DATE_TYPE,
    START_DATE,
    END_DATE,
    ACTIVITY_MASTERY_PATH,
    TEACHER_PATH,
    YEAR_GROUP_PATH,
    DATE_PARAMS
} from './constants';
import { defaultState as defaultActiveFilters } from '../components/report-filters/redux/active-filters/active-filters-reducer';
import activeFiltersSelectors from '../components/report-filters/redux/active-filters/active-filters-selectors';

/**
 * Responsible for managing the relationship between the route & query params and the
 * active filters in the app
 */
export default class RouteHelper {
    /**
     * Calculates the route we need to load according to the user's selected filters
     * @param {object} activeFilters - corresponds to activeFilters object in redux store
     */
    static calculateRouteFromFilters(activeFilters) {
        let route = this.getBasePath(activeFilters[SELECTED_REPORT].id);
        const yearGroupId = this.getFilterId(
            activeFilters,
            SELECTED_YEAR_GROUP
        );
        const teacherId = this.getFilterId(activeFilters, SELECTED_TEACHER);
        const classId = this.getFilterId(activeFilters, SELECTED_CLASS);
        const groupId = this.getFilterId(activeFilters, SELECTED_GROUP);
        const studentId = this.getFilterId(activeFilters, SELECTED_STUDENT);

        if (studentId != null && studentId !== ALL_STUDENTS) {
            route += `/${CLASS_PATH}/${classId}/${STUDENT_PATH}/${studentId}`;
        } else if (groupId != null && groupId !== ALL_GROUPS) {
            route += `/${CLASS_PATH}/${classId}/${GROUP_PATH}/${groupId}`;
        } else if (classId != null && classId !== ALL_CLASSES) {
            route += `/${CLASS_PATH}/${classId}`;
        } else if (teacherId != null && teacherId !== ALL_TEACHERS) {
            if (yearGroupId != null && yearGroupId !== ALL_YEARS) {
                route += `/${YEAR_GROUP_PATH}/${yearGroupId}`;
            }
            route += `/${TEACHER_PATH}/${teacherId}`;
        } else if (yearGroupId != null && yearGroupId !== ALL_YEARS) {
            route += `/${YEAR_GROUP_PATH}/${yearGroupId}`;
        }

        return route;
    }

    /**
     * Calculates the date query params from the active filters
     */
    static calculateParamsFromFilters(activeFilters) {
        const selectedDate = activeFilters[SELECTED_DATE];
        const { startDate, endDate } = selectedDate;

        let params = `dateType=${DATE_TYPE_MAP[selectedDate.type]}`;
        if (startDate) {
            params += `&startDate=${startDate}`;
        }
        if (endDate) {
            params += `&endDate=${endDate}`;
        }
        return params;
    }

    /**
     * Calculates the user's active filters based on their route
     * Route should be in one of 7 formats:
     *  base (all classes or all years): /activity-mastery
     *  year group: /activity-mastery/year/4
     *  teacher: /activity-mastery/teacher/5678
     *  teacher within year group: /activity-mastery/year/4/teacher/5678
     *  single class: /activity-mastery/class/21
     *  group: /activity-mastery/class/21/group/1
     *  student: /activity-mastery/class/21/student/1234
     *
     * Selected date type and start/end date come as query params
     */
    static calculateFiltersFromRoute(
        route,
        params,
        allClasses,
        studentsByGroup,
        yearGroups,
        teachers,
        currentActiveFilters
    ) {
        let activeFilters = { ...defaultActiveFilters };
        const segments = route.split('/').filter((segment) => segment);

        const reportType = this.getReportType(`/${segments[0]}`);
        activeFilters[SELECTED_REPORT] = {
            id: reportType,
            name: reportType
        };

        if (segments.length > 2) {
            activeFilters = {
                ...activeFilters,
                ...this.getFiltersFromPageType(
                    segments,
                    allClasses,
                    studentsByGroup,
                    yearGroups,
                    teachers,
                    currentActiveFilters
                )
            };
        }
        activeFilters[SELECTED_DATE] = this.calculateDateFiltersFromParams(
            params
        );

        return activeFilters;
    }

    /**
     * Calculates the filters from the route when it has a page type eg. activity-mastery/class/34
     * Applies to year group, teacher and any class routes (class, group, student)
     */
    static getFiltersFromPageType(
        segments,
        allClasses,
        studentsByGroup,
        yearGroups,
        teachers,
        currentActiveFilters
    ) {
        const pageType = segments[1];
        const itemId = +segments[2];

        switch (pageType) {
            case CLASS_PATH: {
                let childPageType = CLASS_PATH;
                let childPageItemId;

                if (segments.length > 3) {
                    childPageType = segments[3];
                    childPageItemId = +segments[4];
                }
                return this.getFiltersForChildPage(
                    itemId,
                    childPageType,
                    childPageItemId,
                    allClasses,
                    studentsByGroup,
                    currentActiveFilters
                );
            }

            case YEAR_GROUP_PATH: {
                const filters = {};
                if (segments.length > 3) {
                    const teacherId = +segments[4];
                    const teacher = teachers.find((t) => t.value === teacherId);
                    const name = teacher.label;
                    filters[SELECTED_TEACHER] = { id: teacherId, name };
                }

                const yearGroup = yearGroups.find((y) => y.id === itemId);
                const name = yearGroup.year;
                filters[SELECTED_YEAR_GROUP] = { id: itemId, name };

                return filters;
            }

            case TEACHER_PATH: {
                const teacher = teachers.find((t) => t.value === itemId);
                const name = teacher.label;
                return {
                    [SELECTED_TEACHER]: { id: itemId, name }
                };
            }

            default:
                console.warn('Unhandled path segment', pageType); // eslint-disable-line no-console
                return {};
        }
    }

    static calculateDateFiltersFromParams(params) {
        const dateTypeParam = UrlHelper.getQueryParamValue(DATE_TYPE, params);
        if (dateTypeParam != null) {
            const dateType = DATE_PARAM_MAP[dateTypeParam];
            if (dateType) {
                return {
                    type: dateType,
                    startDate:
                        UrlHelper.getQueryParamValue(START_DATE, params) ||
                        null,
                    endDate:
                        UrlHelper.getQueryParamValue(END_DATE, params) || null
                };
            }
        }
        return {
            type: DatePicker.THIS_SCHOOL_YEAR,
            startDate: null,
            endDate: null
        };
    }

    /**
     * Checks value of each date param in the params string provided
     * Returns true if any of them are different
     */
    static areDateParamsDifferent = (params, prevParams) =>
        DATE_PARAMS.some((dateParam) => {
            const paramValue = UrlHelper.getQueryParamValue(dateParam, params);
            const prevParamValue = UrlHelper.getQueryParamValue(
                dateParam,
                prevParams
            );
            // handle special case where no dateType param is the same as dateType=2
            if (dateParam === DATE_TYPE) {
                const thisSchoolYearType = `${
                    DATE_TYPE_MAP[DatePicker.THIS_SCHOOL_YEAR]
                }`;
                if (
                    (paramValue === thisSchoolYearType &&
                        prevParamValue == null) ||
                    (paramValue == null &&
                        prevParamValue === thisSchoolYearType)
                ) {
                    return false;
                }
            }
            return paramValue !== prevParamValue;
        });

    static isAllYearsRoute = (isAdmin, activeFilters) => {
        const selectedYearGroupId = activeFilters[SELECTED_YEAR_GROUP].id;
        const selectedTeacherId = activeFilters[SELECTED_TEACHER].id;

        return (
            isAdmin === true &&
            (selectedYearGroupId == null ||
                selectedYearGroupId === ALL_YEARS) &&
            (selectedTeacherId == null || selectedTeacherId === ALL_TEACHERS)
        );
    };

    static isLandingPage = (isAdmin, activeFilters) => {
        if (isAdmin === true) {
            return (
                activeFilters[SELECTED_YEAR_GROUP].id === ALL_YEARS &&
                activeFilters[SELECTED_TEACHER].id === ALL_TEACHERS &&
                activeFilters[SELECTED_CLASS].id === ALL_CLASSES
            );
        }
        return activeFilters[SELECTED_CLASS].id === ALL_CLASSES;
    };

    static getFilterId(activeFilters, filterName) {
        return activeFiltersSelectors.selectItemId(filterName)({
            activeFilters
        });
    }

    static calculateSelectedClassFromRoute(route, allClasses) {
        const segments = route.split('/').filter((segment) => segment);
        if (segments.length > 2 && segments[1] === CLASS_PATH) {
            const classId = +segments[2];
            return allClasses.find((classItem) => classItem.id === classId);
        }
        return null;
    }

    static getBasePath(reportType) {
        return REPORT_TYPE_TO_PATH_MAP[reportType] || ACTIVITY_MASTERY_PATH;
    }

    static getReportType(basePath) {
        for (const reportType in REPORT_TYPE_TO_PATH_MAP) {
            if (REPORT_TYPE_TO_PATH_MAP[reportType] === basePath) {
                return reportType;
            }
        }
        return null;
    }

    static getFiltersForChildPage(
        classId,
        childPageType,
        childPageItemId,
        allClasses,
        studentsByGroup,
        currentActiveFilters
    ) {
        const selectedClass = allClasses.find((c) => c.id === classId);
        if (!selectedClass) {
            return {};
        }

        let selectedYearGroup = currentActiveFilters[SELECTED_YEAR_GROUP];
        if (selectedYearGroup.id === null) {
            selectedYearGroup = defaultActiveFilters[SELECTED_YEAR_GROUP];
        }

        let selectedTeacher = currentActiveFilters[SELECTED_TEACHER];
        if (selectedTeacher.id === null) {
            selectedTeacher = defaultActiveFilters[SELECTED_TEACHER];
        }

        const filters = {
            [SELECTED_CLASS]: { id: classId, name: selectedClass.name },
            [SELECTED_YEAR_GROUP]: selectedYearGroup,
            [SELECTED_TEACHER]: selectedTeacher
        };

        switch (childPageType) {
            case GROUP_PATH: {
                const selectedGroup = studentsByGroup.find(
                    (group) => group.id === childPageItemId
                );
                if (selectedGroup) {
                    filters[SELECTED_GROUP] = {
                        id: childPageItemId,
                        name: selectedGroup.name
                    };
                }
                break;
            }
            case STUDENT_PATH: {
                const selectedGroup = studentsByGroup.find(
                    (group) =>
                        group.id !== ALL_GROUPS &&
                        group.students.find(
                            (student) => student.value === childPageItemId
                        )
                );
                if (selectedGroup) {
                    const selectedStudent = selectedGroup.students.find(
                        (student) => student.value === childPageItemId
                    );
                    filters[SELECTED_GROUP] = {
                        id: selectedGroup.id,
                        name: selectedGroup.name
                    };
                    filters[SELECTED_STUDENT] = {
                        id: childPageItemId,
                        name: selectedStudent.label
                    };
                }
                break;
            }
            default:
        }
        return filters;
    }
}



// WEBPACK FOOTER //
// ./helpers/route-helper.js